import { Battery } from "lucide-react";
import type { BatteryOutlook, Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { cn } from "../../lib/cn.js";
import { formatUnit } from "../../lib/format.js";

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * State of charge as the design's ring gauge.
 *
 * The ring is `aria-hidden` and the percentage beside it is real text: a
 * circle whose meaning is carried by an arc length is not readable, and
 * section 4 asks for the value itself rather than a picture of it. The state
 * underneath is a word, not only the pulsing dot, for the same reason.
 */
export function BatteryStatusWidget({
  reading,
  outlook,
  onExpand,
  className,
}: {
  reading: Reading;
  outlook: BatteryOutlook | null;
  onExpand?: () => void;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, reading.socPct));
  const state =
    reading.batteryPowerW < 0 ? "Charging" : reading.batteryPowerW > 0 ? "Discharging" : "Idle";

  return (
    <Card
      onExpand={onExpand}
      title="Battery Status"
      headerRight={
        <span className="tabular flex items-center gap-1.5 rounded-full border border-line bg-line/40 px-2.5 py-1 text-[11px] font-medium text-ink-muted">
          <Battery size={13} aria-hidden="true" className="text-accent-2" />
          <span>
            {outlook === null ? "Capacity not set" : formatUnit(outlook.capacityKwh, "kWh")}
          </span>
        </span>
      }
      className={cn("flex flex-col justify-between", className)}
    >
      <div className="relative my-auto flex items-center justify-center py-2">
        <div className="relative flex h-36 w-36 items-center justify-center sm:h-40 sm:w-40">
          <svg
            aria-hidden="true"
            viewBox="0 0 130 130"
            className="h-full w-full -rotate-90 transform"
          >
            <circle
              cx="65"
              cy="65"
              r={RADIUS + 6}
              fill="none"
              className="stroke-line"
              strokeWidth="2"
              strokeDasharray="2 6"
              strokeLinecap="round"
            />
            <circle
              cx="65"
              cy="65"
              r={RADIUS}
              fill="none"
              className="stroke-line/60"
              strokeWidth="8"
            />
            <circle
              cx="65"
              cy="65"
              r={RADIUS}
              fill="none"
              className="stroke-accent transition-all duration-1000 ease-out"
              strokeWidth="8.5"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={CIRCUMFERENCE - (pct / 100) * CIRCUMFERENCE}
              strokeLinecap="round"
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="tabular text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {pct}%
            </span>
            <span className="mt-0.5 flex items-center gap-1.5">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-2" />
              <span className="text-xs font-medium text-ink-muted">{state}</span>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
